// src/lib/imageUtils.js
// Client-side image compression for chat image uploads
// Compresses PNG/JPEG to target size before uploading

/**
 * Compress an image file to target size
 * @param {File} file - Original image file
 * @param {number} maxKB - Max size in KB (default 500)
 * @returns {Promise<Blob>} - Compressed image blob
 */
export async function compressImage(file, maxKB = 500) {
  return new Promise((resolve, reject) => {
    const maxBytes = maxKB * 1024

    // If already small enough, return as-is
    if (file.size <= maxBytes) {
      resolve(file)
      return
    }

    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(url)

      const canvas = document.createElement('canvas')
      let { width, height } = img

      // Calculate scale to reduce file size
      const ratio = Math.sqrt(maxBytes / file.size)
      width  = Math.floor(width  * ratio)
      height = Math.floor(height * ratio)

      // Minimum dimensions
      width  = Math.max(width,  100)
      height = Math.max(height, 100)

      canvas.width  = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img, 0, 0, width, height)

      // Try different quality levels until under limit
      let quality = 0.85
      const tryCompress = () => {
        canvas.toBlob(
          blob => {
            if (!blob) { reject(new Error('Compression failed')); return }
            if (blob.size <= maxBytes || quality <= 0.1) {
              resolve(blob)
            } else {
              quality -= 0.1
              tryCompress()
            }
          },
          'image/jpeg',
          quality
        )
      }

      tryCompress()
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }

    img.src = url
  })
}

/**
 * Check if user can send an image today
 * Returns { canSend, remaining, limit }
 */
export async function checkImageLimit(supabase, userId, tier) {
  const limits = { gold: 3, premium: 7, platinum: 10 }
  const limit  = limits[tier] || 3

  const today = new Date().toISOString().split('T')[0]

  const { data } = await supabase
    .from('image_usage')
    .select('count')
    .eq('user_id', userId)
    .eq('usage_date', today)
    .single()

  const used      = data?.count || 0
  const canSend   = used < limit
  const remaining = Math.max(0, limit - used)

  return { canSend, remaining, limit, used }
}

/**
 * Increment image usage count for today
 */
export async function incrementImageUsage(supabase, userId) {
  const today = new Date().toISOString().split('T')[0]

  await supabase.rpc('increment_image_usage', {
    p_user_id: userId,
    p_date: today
  }).catch(async () => {
    // Fallback if RPC doesn't exist — upsert manually
    const { data: existing } = await supabase
      .from('image_usage')
      .select('count')
      .eq('user_id', userId)
      .eq('usage_date', today)
      .single()

    if (existing) {
      await supabase
        .from('image_usage')
        .update({ count: existing.count + 1 })
        .eq('user_id', userId)
        .eq('usage_date', today)
    } else {
      await supabase
        .from('image_usage')
        .insert({ user_id: userId, usage_date: today, count: 1 })
    }
  })
}

/**
 * Check if images are enabled globally (admin setting)
 */
export async function getImageSettings(supabase) {
  const { data } = await supabase
    .from('image_settings')
    .select('*')
    .eq('id', 1)
    .single()
  return data || { enabled: true, gold_daily_limit: 3, premium_daily_limit: 7, platinum_daily_limit: 10 }
}
